/**
 * The five media kinds' metadata shapes.
 *
 * Out of `storage/schema/media.ts`, the last of that directory's three modules, so
 * `src/storage/schema/` is deleted with this commit. `docs/target-structure.md:84` gives `entries`
 * the metadata shape, the guard and the predicates.
 *
 * **Five kinds, one implementation — not one type.** `MediaEntry` is the union below, and binding
 * `EntryOf`'s five media keys to it compiles while discarding every per-kind type: `registry.book`
 * would narrow to the union and `metadata.author` would read as `unknown` instead of `string`.
 * `EntryOf` keys each kind to its own entry type, and one `mediaSlice<K>()` factory bound five times
 * is what "one slice" means once the types are honest (#359).
 *
 * `ArtistMeta` and `ReleaseMeta` are already the identical type, so two of the five share more than
 * an implementation.
 */

import type { Entry } from '@/document'

// ============================================================================
// Book Entry Types
// ============================================================================

/**
 * Book reading status as the analysis and the library cards write it.
 *
 * Out of `types/analysis.ts` on #397: the three surfaces that read it — `book-card.tsx`,
 * `movie-card.tsx` and `movie-row.tsx` — are this slice's, and leaving the declaration in the file
 * step 4 moves to `capture/analysis/` would give `entries/` an import of `capture/`. The domain
 * table gives `entries` the metadata shape, so the vocabulary lands here and
 * `capture/analysis/schemas.ts` imports it back through `@/entries`.
 *
 * It is **not** `SecondBrainBookStatus` above: the two vocabularies disagree on every member and
 * nothing collapses them today.
 */
export const BOOK_STATUSES = ['want_to_read', 'reading', 'read'] as const

/** A book's reading status, as analysis emits it. */
export type BookStatus = (typeof BOOK_STATUSES)[number]

/**
 * Book reading status.
 */
export type SecondBrainBookStatus =
  | 'to_read'
  | 'reading'
  | 'finished'
  | 'abandoned'

/**
 * Metadata for book entries.
 */
export interface BookMeta {
  /** Book author */
  author: string
  /** Reading status */
  status: SecondBrainBookStatus
  /** Date started reading (ISO string) */
  dateStarted?: string
  /** Date finished reading (ISO string) */
  dateFinished?: string
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown
}

/**
 * Book entry with typed metadata.
 */
export interface BookEntry extends Omit<Entry, 'type' | 'metadata'> {
  type: 'book'
  metadata: BookMeta
}

// ============================================================================
// Movie Entry Types
// ============================================================================

/**
 * Whether a movie is watched. Out of `types/analysis.ts` on #397, for the reason `BOOK_STATUSES`
 * carries above.
 */
export const MOVIE_STATUSES = ['want_to_watch', 'seen'] as const

/** A movie's watch status, as analysis emits it. */
export type MovieStatus = (typeof MOVIE_STATUSES)[number]

/**
 * Metadata for movie entries.
 */
export interface MovieMeta {
  /** Movie director */
  director?: string
  /** Release year */
  year?: number
  /** Runtime in minutes */
  runtime?: number
  /** Cast members */
  cast?: string[]
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown
}

/**
 * Movie entry with typed metadata.
 */
export interface MovieEntry extends Omit<Entry, 'type' | 'metadata'> {
  type: 'movie'
  metadata: MovieMeta
}

// ============================================================================
// Music Media Types
// ============================================================================

/**
 * Metadata for media entries (artist, release).
 */
export interface MediaMeta {
  /** Creator/author name */
  creator?: string
  /** URL to the media */
  url?: string
  /** Release date (ISO string) */
  releaseDate?: string
  /** Associated label name */
  label?: string
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown
}

/** Alias for artist metadata */
export type ArtistMeta = MediaMeta

/** Alias for release metadata */
export type ReleaseMeta = MediaMeta

/**
 * Artist entry with typed metadata.
 */
export interface ArtistEntry extends Omit<Entry, 'type' | 'metadata'> {
  type: 'artist'
  metadata: ArtistMeta
}

/**
 * Release entry with typed metadata.
 */
export interface ReleaseEntry extends Omit<Entry, 'type' | 'metadata'> {
  type: 'release'
  metadata: ReleaseMeta
}

// ============================================================================
// Label Entry Types
// ============================================================================

/**
 * Metadata for music label entries.
 */
export interface LabelMeta {
  /** Label website URL */
  website?: string
  /** Year the label was founded */
  foundedYear?: number
  /** Label location/headquarters */
  location?: string
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown
}

/**
 * Label entry with typed metadata.
 */
export interface LabelEntry extends Omit<Entry, 'type' | 'metadata'> {
  type: 'label'
  metadata: LabelMeta
}

// ============================================================================
// Metadata Map
// ============================================================================

/**
 * Type map for media entry metadata by entry type.
 * Used for type-safe metadata access.
 */
export interface MediaMetadataMap {
  book: BookMeta
  movie: MovieMeta
  artist: MediaMeta
  release: MediaMeta
  label: LabelMeta
}

// ============================================================================
// Union Types
// ============================================================================

/**
 * Union of all media typed entries.
 */
export type MediaEntry =
  | BookEntry
  | MovieEntry
  | ArtistEntry
  | ReleaseEntry
  | LabelEntry

/**
 * The five kinds this slice covers, as a value.
 *
 * The array is the declaration and the type reads off it, so a caller that needs to test a widened
 * string at runtime — `api/capture/route.ts` does, twice — has one list to test against rather than
 * a second copy. #397 collapsed `types/analysis.ts`'s `MEDIA_TYPES` and `useEntryAnalysis.ts`'s onto
 * this one; both spelled the same five members.
 */
export const MEDIA_KINDS = [
  'book',
  'movie',
  'artist',
  'release',
  'label',
] as const

/** The five kinds this slice covers. */
export type MediaKind = (typeof MEDIA_KINDS)[number]
